import { modelOptions, post, prop } from "@typegoose/typegoose";
import { z } from "zod";
import { getZodSchemaFromClass, validate } from "../../../zod-decorator/index.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../../schemaBase.js";
import type { InboundMediaFieldsWithDefaults, InboundMediaSchemaTypes } from "../../types/inboundMedia.d.js";
import type { DtoValidatorTypes } from "../../validatableClass.js";

const SCHEMA_KEY = "InboundMedia" as const;

/**
 * One attachment on an inbound message. Only the provider's reference is kept, never the bytes — and provider
 * media URLs expire and generally need the provider's own credentials, so anything we keep must be copied first.
 */
@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<InboundMedia>>(...addSchemaKeyHook(SCHEMA_KEY))
export class InboundMedia extends SchemaBase<InboundMedia, InboundMediaFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    /** Where the provider says the attachment lives. Not fetched by ingestion; bounded because it is stored. */
    @validate({ zodValidator: z.url().max(2048) })
    @prop({ type: () => String, required: true })
    public url!: string;

    /**
     * A record, not a gate: the check only asks for `type/subtype` and keeps parameters verbatim — WhatsApp voice
     * notes arrive as `audio/ogg; codecs=opus`.
     */
    @validate({
        zodValidator: z
            .string()
            .max(255)
            .regex(/^[a-zA-Z0-9!#$&^_.+-]+\/[a-zA-Z0-9!#$&^_.+-]+\s*(;.*)?$/, "must be a media type, e.g. image/jpeg"),
    })
    @prop({ type: () => String, required: true })
    public contentType!: string;

    static getters = {} as const satisfies Record<string, (self: InboundMedia) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<InboundMediaSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<InboundMedia, InboundMediaFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

InboundMedia.dtoValidators = getZodSchemaFromClass(
    InboundMedia,
) as unknown as DtoValidatorTypes<InboundMediaSchemaTypes>;
